// CAMERA DIN SPATELE GEAMULUI («să se vadă foarte puțin în interior»,
// Cătălin, 15.10.2026). Nu se desenează mobilă și pereți: dreptunghiul din
// spatele geamului poartă un shader de «interior mapping» — raza dinspre
// cameră trece prin dreptunghi și lovește o cutie închipuită în spatele lui
// (pardoseala, tavanul, peretele din fund, cei laterali), cu perspectiva
// corectă din orice unghi. Fiecare cameră are lumina ei (întuneric, penumbră,
// lampa aprinsă) și, unele, o perdea la geam — fațada nu mai are ferestre
// identice, negre.
//
// Datele cutiei stau pe vârfurile dreptunghiului, ca toate fundalurile să se
// unească într-o singură plasă: originea (colțul de la pardoseală, pe planul
// fundalului), direcția feței, normala spre cameră, lățimea și adâncimea ei,
// înălțimea până la tavan, sămânța.
import * as THREE from "three";

/** Încăperea din spatele unui gol, în reperul lui: `x` în lungul feței de la
 *  originea golului, adâncimea spre interior, `z` de la pardoseală. */
export interface Incapere {
  /** Marginile camerei pe `x` (mai largă decât golul). */
  x0: number;
  x1: number;
  /** Cât de adâncă e (spre interior) și cât de înaltă (pardoseală → tavan). */
  adancime: number;
  inaltime: number;
  /** Golul însuși pe `x`: unde poate sta perdeaua. */
  gol0: number;
  gol1: number;
  /** Sămânța: lumina și perdeaua camerei (aceeași cameră → același aspect). */
  samanta: number;
}

/** Sămânța unei încăperi din poziția ei: stabilă de la o desenare la alta. */
export function samantaDin(x: number, y: number, z: number): number {
  const s = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719) * 43758.5453;
  return s - Math.floor(s);
}

/** Dreptunghiul-fundal din spatele unui gol, cu cutia camerei pe vârfuri.
 *  `a` = originea golului în plan, `u` = direcția feței, `n` = normala SPRE
 *  AFARĂ; dreptunghiul stă la `y` pe normală, de la `xa` la `xb` și de la
 *  `za` la `zb` (cote absolute); `pod` = cota pardoselii. */
export function fundalCamerei(a: number[], u: number[], n: number[], y: number,
                              xa: number, xb: number, za: number, zb: number,
                              pod: number, c: Incapere): THREE.BufferGeometry {
  const P = (x: number, z: number) => [a[0] + u[0] * x + n[0] * y, a[1] + u[1] * x + n[1] * y, z];
  const v = [P(xa, za), P(xb, za), P(xb, zb), P(xa, za), P(xb, zb), P(xa, zb)];
  // înfășurarea spre afară (ca pe `Trunchi`): (v1−v0)×(v2−v0) pe normala lumii
  const e1 = [v[1][0] - v[0][0], v[1][1] - v[0][1], v[1][2] - v[0][2]];
  const e2 = [v[2][0] - v[0][0], v[2][1] - v[0][1], v[2][2] - v[0][2]];
  const gx = e1[1] * e2[2] - e1[2] * e2[1], gy = e1[2] * e2[0] - e1[0] * e2[2];
  if (gx * n[0] + gy * n[1] < 0) { [v[1], v[2]] = [v[2], v[1]]; [v[4], v[5]] = [v[5], v[4]]; }
  const poz: number[] = [], nrm: number[] = [], o: number[] = [], du: number[] = [], dn: number[] = [];
  const box: number[] = [], gol: number[] = [], sem: number[] = [];
  const O = [a[0] + n[0] * y, a[1] + n[1] * y, pod];
  for (const q of v) {
    poz.push(...q); nrm.push(n[0], n[1], 0);
    o.push(...O); du.push(u[0], u[1]); dn.push(-n[0], -n[1]);
    box.push(c.x0, c.x1, c.adancime, c.inaltime); gol.push(c.gol0, c.gol1); sem.push(c.samanta);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(poz, 3));
  g.setAttribute("normal", new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute("aO", new THREE.Float32BufferAttribute(o, 3));
  g.setAttribute("aU", new THREE.Float32BufferAttribute(du, 2));
  g.setAttribute("aN", new THREE.Float32BufferAttribute(dn, 2));
  g.setAttribute("aBox", new THREE.Float32BufferAttribute(box, 4));
  g.setAttribute("aGol", new THREE.Float32BufferAttribute(gol, 2));
  g.setAttribute("aSeed", new THREE.Float32BufferAttribute(sem, 1));
  return g;
}

/** Materialul camerelor: cutia închipuită, luminată de ea însăși (ziua, din
 *  afară, o cameră e mult mai întunecată decât fațada). */
export function materialCamera(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    // TĂIETURA PE ETAJ (`taieturaEtaj`) e un plan de decupare pe materiale:
    // fără el camerele de deasupra rămâneau în aer, peste etajul deschis
    clipping: true,
    uniforms: {
      uPerete: { value: new THREE.Color(0xd9d2c5) },
      uPodea: { value: new THREE.Color(0x8a6c50) },
      uTavan: { value: new THREE.Color(0xeeeae2) },
      uPerdea: { value: new THREE.Color(0xf0ebe0) },
      uLampa: { value: new THREE.Color(0xffd9a0) },
    },
    // ADÂNCIMEA LOGARITMICĂ a machetei (`logarithmicDepthBuffer`): fără
    // blocurile ei, shaderul scria adâncimea liniară și corpul din spate,
    // scris logaritmic, îl acoperea peste tot
    vertexShader: /* glsl */ `
      #include <common>
      #include <logdepthbuf_pars_vertex>
      #include <clipping_planes_pars_vertex>
      attribute vec3 aO; attribute vec2 aU; attribute vec2 aN; attribute vec4 aBox;
      attribute vec2 aGol; attribute float aSeed;
      varying vec3 vW; varying vec3 vO; varying vec2 vU; varying vec2 vN; varying vec4 vBox;
      varying vec2 vGol; varying float vSeed;
      void main() {
        vec4 w = modelMatrix * vec4(position, 1.0);
        vW = w.xyz; vO = aO; vU = aU; vN = aN; vBox = aBox; vGol = aGol; vSeed = aSeed;
        vec4 mvPosition = viewMatrix * w;
        gl_Position = projectionMatrix * mvPosition;
        #include <logdepthbuf_vertex>
        #include <clipping_planes_vertex>
      }`,
    fragmentShader: /* glsl */ `
      #include <logdepthbuf_pars_fragment>
      #include <clipping_planes_pars_fragment>
      uniform vec3 uPerete; uniform vec3 uPodea; uniform vec3 uTavan; uniform vec3 uPerdea;
      uniform vec3 uLampa;
      varying vec3 vW; varying vec3 vO; varying vec2 vU; varying vec2 vN; varying vec4 vBox;
      varying vec2 vGol; varying float vSeed;
      float h(float s) { return fract(sin(s * 91.345 + 7.13) * 43758.5453); }
      void main() {
        #include <clipping_planes_fragment>
        #include <logdepthbuf_fragment>
        vec3 d = normalize(vW - cameraPosition);
        vec3 p = vW - vO;
        float px = dot(p.xy, vU), py = dot(p.xy, vN), pz = p.z;
        float dx = dot(d.xy, vU), dy = max(dot(d.xy, vN), 1e-4), dz = d.z;
        // ieșirea razei din cutie: peretele din fund, unul lateral, pardoseala sau tavanul
        float tx = dx > 0.0 ? (vBox.y - px) / max(dx, 1e-4) : (vBox.x - px) / min(dx, -1e-4);
        float ty = (vBox.z - py) / dy;
        float tz = dz > 0.0 ? (vBox.w - pz) / max(dz, 1e-4) : (0.0 - pz) / min(dz, -1e-4);
        float t = min(ty, min(tx, tz));
        vec3 hit = vec3(px + dx * t, py + dy * t, pz + dz * t);
        vec3 c;
        float umbra = 1.0;
        // SĂMÂNȚA ROTUNJITĂ: h() o înmulțește cu 43758, deci și o abatere de
        // 1e-7 din interpolarea pe pixeli a varying-ului ar da altă cameră
        // pe fiecare pixel (zgomot care tremură la mișcarea camerei)
        float s = floor(vSeed * 4096.0 + 0.5) / 4096.0;
        // fiecare cameră cu tonul ei: pereți albi sau calzi, parchet sau gresie
        vec3 perete = mix(uPerete, vec3(0.86, 0.76, 0.62), h(s + 11.0) * 0.6);
        vec3 podea = mix(uPodea, vec3(0.62, 0.60, 0.56), step(0.7, h(s + 13.0)));
        if (t == ty) { c = perete * 0.92; }
        else if (t == tz) { c = dz > 0.0 ? uTavan : podea; umbra = dz > 0.0 ? 0.85 : 1.0; }
        else { c = perete; }
        // colțurile cutiei mai întunecate (lumina ocolită)
        float colt = smoothstep(0.0, 0.35, min(min(hit.x - vBox.x, vBox.y - hit.x),
                                               min(hit.z, vBox.w - hit.z)));
        c *= mix(0.72, 1.0, colt) * umbra;
        // LUMINA CAMEREI: ziua, de afară, o cameră se vede întunecată; spre fund și mai mult
        float zi = mix(0.10, 0.30, h(s));
        c *= zi * mix(1.0, 0.45, clamp(hit.y / vBox.z, 0.0, 1.0));
        // …iar unele au lampa aprinsă: o pată caldă pe tavan și pe fund
        if (h(s + 3.1) > 0.78) {
          vec2 l = vec2((vBox.x + vBox.y) * 0.5, vBox.z * 0.45);
          float r = length(vec2(hit.x - l.x, hit.y - l.y)) + abs(vBox.w - hit.z) * 0.6;
          c += uLampa * 0.22 * exp(-r * 0.9);
        }
        // PERDEAUA, la geam: pe o parte sau pe amândouă, sau deloc. Lată de
        // 10–35 % din gol, dar nu din mai mult de 3 m: pe un front vitrat
        // de zeci de metri (cortina) acoperea celule întregi, etaje întregi
        float k = h(s + 5.3);
        if (k > 0.55) {
          float lat = min(vGol.y - vGol.x, 3.0);
          float st = vGol.x + lat * (0.10 + 0.25 * h(s + 6.1));
          float dr = vGol.y - lat * (0.10 + 0.25 * h(s + 8.7));
          bool pe = (k > 0.78 && px < st) || (k < 0.9 && px > dr);
          if (pe && pz > 0.05) {
            // FALDURILE (perioada 16,5 cm) se sting când un pixel acoperă
            // mai mult de ~1/6 din perioadă: sub pragul ăsta sinusul eșantionat
            // pe pixeli făcea moar și tremura la orice mișcare a camerei
            float faza = px * 38.0;
            float amp = 0.12 * (1.0 - smoothstep(1.0, 2.5, fwidth(faza)));
            float falduri = 1.0 - amp + amp * sin(faza);
            c = mix(c, uPerdea * zi * 1.25 * falduri, 0.6);
          }
        }
        gl_FragColor = vec4(c, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
  });
}

/** STICLA: reflexia crește spre unghiurile razante (Fresnel), privită din
 *  față se vede prin ea camera din spate. Aceeași pentru ferestre și pentru
 *  câmpul vizibil al peretelui cortină (cu nuanța lui). */
export function materialGeam(culoare: number | THREE.Color, opacitate = 0.3): THREE.MeshPhysicalMaterial {
  // NUANȚA E A STICLEI, NU O VOPSEA: stratul străveziu întunecă ce e în spate
  // (o nuanță deschisă, luminată de soare, spăla camera într-un gri), iar
  // luciul vine din reflexia cerului. O SINGURĂ FAȚĂ: geamul e o cutie, iar
  // cele două fețe una peste alta dublau și opacitatea, și reflexia
  const m = new THREE.MeshPhysicalMaterial({
    color: new THREE.Color(culoare).multiplyScalar(0.42), roughness: 0.03, metalness: 0.1,
    transparent: true, opacity: opacitate, envMapIntensity: 1.4, specularIntensity: 1, ior: 1.5,
    side: THREE.FrontSide,
  });
  m.onBeforeCompile = (sh) => {
    sh.fragmentShader = sh.fragmentShader.replace(
      "#include <opaque_fragment>",
      `float fresnelSticla = pow(1.0 - clamp(abs(dot(normalize(geometryNormal), normalize(geometryViewDir))), 0.0, 1.0), 3.0);
       diffuseColor.a = mix(diffuseColor.a, 1.0, fresnelSticla);
       #include <opaque_fragment>`);
  };
  m.customProgramCacheKey = () => `sticla-fresnel`;
  return m;
}
